import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { BASE_URL, getFetcherPublic } from '../../../../services/api';
import { jadikanTautan } from '../../../../services/tautan';
import Ikon from './Ikon';
import { GambarAman, IlustrasiKartu, JudulSeksi, LabelSeksi, TautanPanah, Wadah, teksPolos } from './Elemen';

/*
 * Seksi-seksi yang dipakai bersama oleh Beranda dan halaman topik Informasi.
 * Tata letak mengikuti frame Figma 1440px.
 */

const IKON_KARTU = ['labu', 'tunas', 'buku'];

/* Hero hijau muda: judul besar di kiri, foto membulat 20px di kanan. */
export function HeroMisi({ judul, pengantar, gambar }) {
    return (
        <section className="bg-pb-kabut">
            <Wadah className="grid items-center gap-10 py-12 lg:grid-cols-[1fr_440px] lg:gap-[72px] lg:pb-14 lg:pt-[60px]">
                <div className="flex max-w-[768px] flex-col gap-6">
                    <h1 className="!font-body text-4xl font-semibold leading-[1.08] tracking-[-1.5px] text-pb-ink md:text-5xl lg:text-[60px] lg:tracking-[-2.6px]">
                        {judul}
                    </h1>
                    {pengantar && <p className="max-w-[620px] font-body text-base leading-[1.65] text-pb-teks md:text-lg">{pengantar}</p>}
                </div>
                <div className="relative h-64 overflow-hidden rounded-[20px] bg-pb-hutan sm:h-80 lg:h-[408px]">
                    {/* Dua orbit tipis di belakang foto, seperti di Figma - hanya
                        terlihat bila foto belum termuat atau transparan. */}
                    <span className="absolute left-[9%] top-[10%] aspect-square w-[82%] rounded-full border border-[#52715a]" aria-hidden="true" />
                    <img src={gambar} alt="" className="absolute inset-0 size-full object-cover" />
                </div>
            </Wadah>
        </section>
    );
}

/* Kartu konten bergaya "Topic card": ilustrasi, label, judul, ringkasan, tautan. */
export function KartuKonten({ label, judul, uraian, tautan, ke, href, ikon, gambar, oranye = false }) {
    return (
        // min-w-0: sel grid tidak boleh melebar mengikuti isi (judul panjang) di layar sempit.
        <article className="flex min-w-0 flex-col gap-5">
            {ke ? (
                <Link to={ke} tabIndex={-1} aria-hidden="true" className="block">
                    <IlustrasiKartu ikon={ikon} gambar={gambar} />
                </Link>
            ) : (
                <IlustrasiKartu ikon={ikon} gambar={gambar} />
            )}
            <div className="flex flex-col gap-3">
                {label && <LabelSeksi>{label}</LabelSeksi>}
                <h3 className="!font-body text-[23px] font-semibold leading-[1.35] tracking-[-0.6px] text-pb-ink">
                    {ke ? (
                        <Link to={ke} className="hover:underline underline-offset-4">
                            {judul}
                        </Link>
                    ) : (
                        judul
                    )}
                </h3>
                {uraian && <p className="line-clamp-2 font-body text-[15px] leading-[1.65] text-pb-teks">{uraian}</p>}
            </div>
            {tautan && (ke || href) && (
                <TautanPanah ke={ke} href={href} oranye={oranye}>
                    {tautan}
                </TautanPanah>
            )}
        </article>
    );
}

/* Kepala seksi: label + judul di kiri, tautan "Jelajahi ..." di kanan. */
export function KepalaSeksi({ label, judul, tautan, ke, gelap = false }) {
    return (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-col gap-3.5">
                {label && <LabelSeksi gelap={gelap}>{label}</LabelSeksi>}
                <JudulSeksi gelap={gelap}>{judul}</JudulSeksi>
            </div>
            {tautan && (
                <TautanPanah ke={ke} gelap={gelap} className="shrink-0 sm:pb-2">
                    {tautan}
                </TautanPanah>
            )}
        </div>
    );
}

/* Alamat baca untuk satu berita/artikel. */
export function tautanKonten(item) {
    const jenis = item?.categories?.[0]?.type;
    return jenis === 'NEWS' ? `/berita/${item.slug}` : `/artikel/${item.slug}`;
}

/*
 * Berita & artikel terbaru (gabungan dua tabel, urut tanggal). `kategori` opsional:
 * nama kategori untuk halaman topik.
 */
export function useKontenTerbaru(kategori, jumlah = 3) {
    const filter = kategori ? `&categoryName=${encodeURIComponent(kategori)}` : '';
    const { data: berita, isLoading: a } = useSWR(
        `/api/content/search-news?page=1&limit=${jumlah}&sortBy=createdAt${filter}`,
        getFetcherPublic
    );
    const { data: artikel, isLoading: b } = useSWR(
        `/api/content/search-articles?page=1&limit=${jumlah}&sortBy=createdAt${filter}`,
        getFetcherPublic
    );
    const daftar = useMemo(
        () =>
            [...(berita?.data ?? []), ...(artikel?.data ?? [])]
                .sort((x, y) => new Date(y.createdAt) - new Date(x.createdAt))
                .slice(0, jumlah),
        [berita, artikel, jumlah]
    );
    return { daftar, isLoading: a || b };
}

export function KerangkaKartu() {
    return (
        <div className="flex animate-pulse flex-col gap-5" aria-hidden="true">
            <div className="h-28 rounded-md bg-pb-kabut" />
            <div className="h-3 w-20 rounded bg-pb-garis" />
            <div className="h-6 w-4/5 rounded bg-pb-garis" />
            <div className="h-4 w-full rounded bg-pb-garis" />
        </div>
    );
}

export function KartuKontenDb({ item, indeks = 0, oranye = false }) {
    return (
        <KartuKonten
            label={item.categories?.[0]?.name}
            judul={item.title}
            uraian={teksPolos(item.description || item.content)}
            tautan="Baca selengkapnya"
            ke={tautanKonten(item)}
            ikon={IKON_KARTU[indeks % IKON_KARTU.length]}
            gambar={item.photoUrls?.[0] ? `${BASE_URL}${item.photoUrls[0]}` : undefined}
            oranye={oranye}
        />
    );
}

/* Seksi "Berita & Artikel": tiga konten terbaru dari CMS. */
export function SeksiBeritaTerbaru({ kategori, ke = '/berita' }) {
    const { daftar, isLoading } = useKontenTerbaru(kategori);
    return (
        <div className="flex flex-col gap-8">
            <KepalaSeksi label="Berita & Artikel" judul="Berita & Artikel" tautan="Jelajahi berita & artikel" ke={ke} />
            {isLoading ? (
                <div className="grid gap-8 md:grid-cols-3">
                    {[0, 1, 2].map((i) => (
                        <KerangkaKartu key={i} />
                    ))}
                </div>
            ) : daftar.length === 0 ? (
                <p className="rounded-md bg-pb-kabut p-8 text-center font-body text-sm text-pb-teks">
                    Belum ada berita atau artikel untuk topik ini.
                </p>
            ) : (
                <div className="grid gap-10 md:grid-cols-3 md:gap-8">
                    {daftar.map((item, i) => (
                        <KartuKontenDb key={item.id} item={item} indeks={i} />
                    ))}
                </div>
            )}
        </div>
    );
}

/*
 * Pita galeri hijau tua: judul di kiri, empat foto dalam kartu gelap.
 * Bila kategori diberikan tetapi kosong, jatuh kembali ke galeri terbaru umum.
 */
export function SeksiGaleri({ kategori }) {
    const filter = kategori ? `&categoryName=${encodeURIComponent(kategori)}` : '';
    const { data } = useSWR(`/api/content/search-gallery?page=1&limit=4&sortBy=createdAt${filter}`, getFetcherPublic);
    const { data: umum } = useSWR(
        kategori && data && !data.data?.length ? '/api/content/search-gallery?page=1&limit=4&sortBy=createdAt' : null,
        getFetcherPublic
    );
    const daftar = (data?.data?.length ? data.data : umum?.data) ?? [];
    const ke = `/galeri${kategori ? `?kategori=${encodeURIComponent(kategori)}` : ''}`;

    return (
        <section className="bg-pb-hutan">
            <Wadah className="flex flex-col gap-8 py-16 lg:flex-row lg:gap-16">
                <div className="flex shrink-0 items-start justify-between gap-5 lg:w-[122px] lg:flex-col">
                    <div className="flex flex-col gap-5">
                        <LabelSeksi gelap>Galeri</LabelSeksi>
                        <JudulSeksi gelap>Galeri</JudulSeksi>
                    </div>
                    <TautanPanah ke={ke} gelap className="lg:hidden">
                        Jelajahi Galeri
                    </TautanPanah>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <div className="hidden justify-end lg:flex">
                        <TautanPanah ke={ke} gelap>
                            Jelajahi Galeri
                        </TautanPanah>
                    </div>
                    <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                        {daftar.map((g) => (
                            <Link
                                key={g.id}
                                to={ke}
                                className="group flex flex-col gap-3 rounded-md border border-pb-hutan-garis bg-pb-hutan-kartu p-3 sm:p-5"
                            >
                                <span className="block aspect-[221/148] overflow-hidden bg-pb-hutan">
                                    <GambarAman
                                        src={sampulGaleri(g)}
                                        className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        ikonClassName="size-8 text-pb-limau/60"
                                    />
                                </span>
                                <span className="line-clamp-2 font-body text-sm font-medium text-white">{g.title}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </Wadah>
        </section>
    );
}

/* Gambar sampul galeri: foto pertama, atau gambar mini YouTube untuk video. */
export function sampulGaleri(g) {
    if (g?.photoUrls?.[0]) return `${BASE_URL}${g.photoUrls[0]}`;
    const id = g?.videoLink?.match(/(?:v=|youtu\.be\/|embed\/)([^&\n?#]+)/)?.[1];
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
}

/*
 * Jawaban FAQ ditulis sebagai teks biasa di CMS. Karakter HTML dilucuti dulu
 * sebelum alamat diubah jadi tautan (sama seperti halaman FAQ lama).
 */
export function lolosHtml(teks) {
    return String(teks ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

export function JawabanFaq({ teks, className = '' }) {
    return (
        <p
            className={`whitespace-pre-line break-words font-body text-sm leading-[1.7] text-pb-teks [&_a]:text-pb-aksen [&_a]:underline ${className}`}
            dangerouslySetInnerHTML={{ __html: jadikanTautan(lolosHtml(teks)) }}
        />
    );
}

/* Satu butir akordeon FAQ bergaris atas, ikon plus/minus di kanan. */
export function ButirFaq({ item, terbuka, onToggle, garisBawah }) {
    const id = `faq-${item.id}`;
    return (
        <div className={`border-t border-pb-garis ${garisBawah ? 'border-b' : ''}`}>
            <h3>
                <button
                    type="button"
                    aria-expanded={terbuka}
                    aria-controls={id}
                    onClick={onToggle}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left font-body text-base font-semibold text-pb-ink"
                >
                    {item.pertanyaan}
                    <Ikon nama={terbuka ? 'minus' : 'plus'} className="size-5 shrink-0 text-pb-aksen" />
                </button>
            </h3>
            {terbuka && (
                <div id={id} className="pb-5">
                    <JawabanFaq teks={item.jawaban} />
                </div>
            )}
        </div>
    );
}

/*
 * Seksi "FAQ & Bantuan": pengantar di kiri, tiga pertanyaan teratas di kanan.
 * `kata` opsional - kata kunci topik untuk mendahulukan FAQ yang relevan.
 */
export function SeksiFaq({ sebutan = 'B3', kata }) {
    const { data } = useSWR('/api/faq/publik', getFetcherPublic);
    const [buka, setBuka] = useState(0);
    const daftar = useMemo(() => {
        const semua = data?.data ?? [];
        if (!kata) return semua.slice(0, 3);
        const cocok = semua.filter((f) => `${f.pertanyaan} ${f.jawaban}`.toLowerCase().includes(kata));
        return [...cocok, ...semua.filter((f) => !cocok.includes(f))].slice(0, 3);
    }, [data, kata]);

    return (
        <section className="border-t border-pb-garis bg-pb-kertas">
            <Wadah className="grid gap-10 py-[72px] lg:grid-cols-[430px_1fr] lg:gap-20">
                <div className="flex flex-col gap-5">
                    <LabelSeksi>FAQ & Bantuan</LabelSeksi>
                    <JudulSeksi>Ada yang ingin Anda ketahui?</JudulSeksi>
                    <p className="font-body text-base leading-relaxed text-pb-teks">
                        Temukan jawaban awal sebelum menggunakan layanan atau menelusuri informasi {sebutan}.
                    </p>
                    <TautanPanah ke="/faq">Lihat semua FAQ</TautanPanah>
                </div>
                <div>
                    {daftar.map((item, i) => (
                        <ButirFaq
                            key={item.id}
                            item={item}
                            terbuka={buka === i}
                            onToggle={() => setBuka(buka === i ? -1 : i)}
                            garisBawah={i === daftar.length - 1}
                        />
                    ))}
                </div>
            </Wadah>
        </section>
    );
}
